import type { PlanOfferInput, PlanTrack } from '../../types/learning-plans';

export interface OfferSubject { gradeIds?: string[]; id: string; slug: string; nameEn: string; nameAr?: string }
export interface OfferGrade { id: string; levelId: string; nameEn: string; nameAr?: string }
export interface OfferLevel { id: string; slug: string; nameEn: string; nameAr?: string }
export type OfferTemplate = 'primary' | 'primary_english' | 'subject' | 'quran';

export const offerTemplates: { key: OfferTemplate; name: string; description: string }[] = [
  { key: 'primary', name: 'مواد الابتدائي', description: 'معلّم شامل لعدة مواد، والإنجليزية في مسار مستقل.' },
  { key: 'primary_english', name: 'مواد الابتدائي مع الإنجليزية', description: 'معلّم شامل للمواد الأساسية ومعلّم متخصص للإنجليزية.' },
  { key: 'subject', name: 'مادة دراسية', description: 'معلّم متخصص لمادة واحدة، مع إمكانية إضافة مواد أخرى بمعلّمين مستقلين.' },
  { key: 'quran', name: 'القرآن', description: 'حفظ وتلاوة في خطة مستقلة لجميع المراحل.' },
];

export function newOffer(): PlanOfferInput {
  return {
    slug: `plan_${crypto.randomUUID().replaceAll('-', '').slice(0, 16)}`,
    nameAr: '', nameEn: '', countryCode: 'SA', curriculum: 'saudi', currency: 'SAR',
    category: 'academic', gradeIds: [], priceCents: 0, trialPriceCents: 0,
    tracks: [newTrack('generalist', 'core')],
    policy: { cycleDays: 28, holdHours: 24, noticeHours: 24, makeupDays: 7, reminderDays: 7, retentionHours: 48 },
    trialPolicy: { eligibilityLimit: 1, studentChangesAllowed: false, studentAbsenceOutcome: 'forfeit' },
  };
}

export function newTrack(model: PlanTrack['model'], key = `track_${crypto.randomUUID().slice(0, 8)}`): PlanTrack {
  return {
    key, model, name: model === 'generalist' ? 'المواد الأساسية' : model === 'quran' ? 'القرآن' : 'مادة دراسية',
    subjectIds: [], weeklyMeetings: model === 'generalist' ? 5 : model === 'quran' ? 3 : 2,
    meetings: model === 'generalist' ? 20 : model === 'quran' ? 12 : 8,
    durationMinutes: model === 'generalist' ? 90 : model === 'quran' ? 30 : 60,
    maxDurationMinutes: model === 'generalist' ? 90 : model === 'quran' ? 45 : 60,
    trialDurationMinutes: model === 'quran' ? 30 : 60, changeAllowance: 2, compensation: '',
  };
}

export function inferTemplate(offer: PlanOfferInput): OfferTemplate {
  if (offer.category === 'quran') return 'quran';
  if (offer.tracks.some((track) => track.model === 'generalist'))
    return offer.tracks.some((track) => track.model === 'specialist') ? 'primary_english' : 'primary';
  return 'subject';
}

export function templateTracks(template: OfferTemplate, subjects: OfferSubject[]): PlanTrack[] {
  const core = newTrack('generalist', 'core');
  const english = { ...newTrack('specialist', 'english'), name: 'الإنجليزية',
    subjectIds: subjects.filter((subject) => subject.slug === 'english').map((subject) => subject.id) };
  const quran = { ...newTrack('quran', 'quran'), subjectIds: subjects.filter((subject) => subject.slug === 'quran').map((subject) => subject.id) };
  return template === 'primary' ? [core] : template === 'primary_english' ? [core, english]
    : template === 'quran' ? [quran] : [newTrack('specialist', 'subject')];
}

export function availableSubjects(model: PlanTrack['model'], subjects: OfferSubject[]): OfferSubject[] {
  return subjects.filter((subject) => model === 'quran' ? subject.slug === 'quran'
    : subject.slug !== 'quran' && (model !== 'generalist' || subject.slug !== 'english'));
}

export function availableGrades(offer: PlanOfferInput, grades: OfferGrade[], levels: OfferLevel[]): OfferGrade[] {
  if (!offer.tracks.some((track) => track.model === 'generalist')) return grades;
  const primary = levels.find((level) => level.slug === 'primary');
  return grades.filter((grade) => grade.levelId === primary?.id);
}

export function changeOfferCountry(offer: PlanOfferInput, countryCode: string): PlanOfferInput {
  return { ...offer, countryCode, gradeIds: [], tracks: offer.tracks.map((track) => ({ ...track, subjectIds: [] })), priceCents: 0, trialPriceCents: 0,
    ...(countryCode === 'SA' ? { curriculum: 'saudi', currency: 'SAR' }
      : countryCode === 'EG' ? { curriculum: 'egyptian', currency: 'EGP' } : {}) };
}

export function prepareOffer(offer: PlanOfferInput): PlanOfferInput {
  return { ...offer, nameAr: offer.nameAr.trim(), nameEn: offer.nameEn.trim() || offer.nameAr.trim() };
}

export type CompensationBasis = 'lesson' | 'cycle';
const BASIS_TEXT: Record<CompensationBasis, string> = { lesson: 'لكل حصة مُقدَّمة', cycle: 'عن الدورة كاملة' };
const BASES: CompensationBasis[] = ['lesson', 'cycle'];

/**
 * Teacher compensation is stored as the free text the teacher is shown. Most
 * offers say the same thing — an amount per delivered lesson or per cycle —
 * so the editor builds that sentence from two fields, and keeps free text for
 * anything that does not fit.
 */
export function composeCompensation(amount: string, basis: CompensationBasis, currency: string): string {
  return /^\d+(\.\d{1,2})?$/.test(amount.trim()) && Number(amount) > 0
    ? `${amount.trim()} ${currency} ${BASIS_TEXT[basis]}` : '';
}

/** The amount and basis a composed sentence came from, or null for custom text. */
export function parseCompensation(text: string): { amount: string; basis: CompensationBasis } | null {
  const match = /^(\d+(?:\.\d{1,2})?) [A-Z]{3} (.+)$/.exec(text.trim());
  const basis = BASES.find((key) => BASIS_TEXT[key] === match?.[2]);
  return match && basis ? { amount: match[1], basis } : null;
}
